<script lang="ts" setup>
import BaseButton from '@/common/components/molecules/baseButton/BaseButton.vue';
import { clickoutside as vClickOutside } from '@/common/directives/clickoutside';
import { setup, Props, Emit } from '@/common/components/molecules/filterSearch/filterSearch.setup';
import FilterSearchDropdownList from '@/common/components/molecules/filterSearch/FilterSearchDropdownList.vue';
import FilterSearchToken from '@/common/components/molecules/filterSearch/FilterSearchToken.vue';
import { wheeloutside as vWheelOutside } from '@/common/directives/wheeloutside';

const props = defineProps<Props>();
const emit = defineEmits<Emit>();

const {
  t,
  logicalOperatorListItems,
  selectedLogicalOperator,
  onChangeLogicalOperator,

  isFilterActive,
  onClickPowerButton,

  isFocus,
  filterMainRef,
  filterSearchRef,
  tokensMoreBtnWrapperRef,
  tokensWrapperRef,
  setElementRef,
  moreCount,
  isExpand,
  expand,
  onClickOutside,
  onWheelOutside,

  isShowDropdown,
  dropdownListInfo,
  dropdownSearchText,
  hoveredDropdownItemIndex,
  isKeyboardNavigating,
  dropdownListRef,
  dropdownListWrapperStyle,
  onClickDropdownItem,

  mainInputRef,
  mainInputValue,
  onCompositionStart,
  onCompositionEnd,
  onInput,
  onKeyEnter,
  onKeyCtrlEnter,
  onKeyUpDown,
  onKeyBackspace,
  onKeyEsc,
  onFocusMainInput,

  tokenList,
  editingToken,
  lastToken,
  hasCompletedToken,
  onClickRemoveToken,
  onClickResumeToken,
  onClickTokenParts,
  isActiveAddMultiValueBtn,
  onClickAddMultiValue,
  onClickClearAll,
  onTokenBackspace,

  filterHistory,
  isShowFilterHistoryList,
  addTokenByHistory,
  removeHistoryItem,
  onClickMoreCount,
} = setup(props, emit);

defineExpose({
  clearFilterSearchToken: onClickClearAll,
});
</script>
<template>
  <div
    ref="filterSearchRef"
    v-click-outside="onClickOutside"
    v-wheel-outside="onWheelOutside"
    :class="['filter-search', { 'filter-search--active': isFilterActive }]"
  >
    <h-stack class="filter-search__logical-operator">
      <base-button
        class="filter-search__power-button"
        @click.stop="onClickPowerButton"
      >
        <ev-icon
          size="small"
          icon="icon-filter-list"
        />
      </base-button>
      <ev-select
        v-model="selectedLogicalOperator"
        :items="logicalOperatorListItems"
        class="size-medium"
        @change="onChangeLogicalOperator"
      />
    </h-stack>
    <div
      ref="filterMainRef"
      :class="['filter-search__filter-main', { focus: isFocus, expand: isExpand }]"
    >
      <div class="filter-search__filter-input-wrapper">
        <div
          ref="tokensMoreBtnWrapperRef"
          :class="['filter-search__tokens-more-btn-wrapper', { expand: isExpand }]"
        >
          <ul
            ref="tokensWrapperRef"
            class="filter-search__tokens-wrapper"
            :style="{
              flexFlow: isExpand ? 'row wrap' : 'row nowrap',
            }"
            @click.stop="expand"
          >
            <li
              v-for="(token, tokenIndex) in tokenList"
              :ref="(el) => setElementRef(el, tokenIndex)"
              :key="tokenIndex"
              class="filter-search__token-wrapper"
            >
              <filter-search-token
                :token-data="token"
                :is-editing-token="token.id === editingToken?.id"
                :is-last-token="token.id === lastToken?.id"
                :dropdown-list-category="dropdownListInfo.category"
                :is-show-dropdown-list="isShowDropdown"
                :is-active-add-multi-value-btn="isActiveAddMultiValueBtn(token)"
                @input="onInput"
                @key-enter="onKeyEnter"
                @key-arrow="(param) => onKeyUpDown(param.event, param.direction)"
                @key-esc="onKeyEsc"
                @key-backspace="() => onTokenBackspace(tokenIndex)"
                @click-parts="onClickTokenParts"
                @click-add-multi-value="onClickAddMultiValue"
                @key-ctrl-enter="onKeyCtrlEnter"
                @click-resume="(t) => onClickResumeToken(t)"
                @remove="() => onClickRemoveToken(tokenIndex, token.id === editingToken?.id)"
              />
            </li>
            <li
              class="filter-search__main-input-wrapper"
              @mousedown="
                (e) => {
                  if (e.button !== 0) e.preventDefault();
                }
              "
            >
              <input
                v-if="!moreCount || isExpand"
                ref="mainInputRef"
                v-model="mainInputValue"
                :placeholder="
                  tokenList.length
                    ? ''
                    : (props.placeholder ?? t('MESSAGE.FILTER_SEARCH_PLACEHOLDER'))
                "
                @input="onInput"
                @focus="onFocusMainInput"
                @compositionstart="onCompositionStart"
                @compositionend="onCompositionEnd"
                @keydown.enter.exact="onKeyEnter"
                @keydown.up.exact="(e) => onKeyUpDown(e, 'up')"
                @keydown.down.exact="(e) => onKeyUpDown(e, 'down')"
                @keydown.delete.exact="onKeyBackspace"
                @keydown.esc.exact.stop="onKeyEsc"
                @keydown.ctrl.enter.exact="onKeyCtrlEnter"
              />
            </li>
          </ul>
          <div
            v-if="moreCount"
            class="filter-search__more-count-button-wrapper"
          >
            <base-button
              tag="button"
              class="filter-search__more-count-button"
              type="button"
              @click.stop="onClickMoreCount"
            >
              {{ isExpand ? moreCount : `+${moreCount}` }}
              <ev-icon
                size="xsmall"
                :icon="isExpand ? 'icon-fill-arrow-up' : 'icon-fill-arrow-down'"
              />
            </base-button>
          </div>
        </div>
        <div
          v-show="hasCompletedToken"
          class="filter-search__clear-all-btn-wrapper"
        >
          <base-button
            type="icon"
            size="small"
            class="filter-search__clear-all-button"
            @click.stop="onClickClearAll"
          >
            <ev-icon icon="icon-close" />
          </base-button>
        </div>
      </div>
      <div
        v-if="isShowDropdown"
        ref="dropdownListRef"
        class="filter-search__dropdown-list-wrapper"
        :style="dropdownListWrapperStyle"
        @mousemove="isKeyboardNavigating = false"
      >
        <filter-search-dropdown-list
          :dropdown-list-info="dropdownListInfo"
          :search-text="dropdownSearchText"
          :hovered-dropdown-item-index="hoveredDropdownItemIndex"
          :show-filter-history-list="isShowFilterHistoryList"
          :filter-history="filterHistory"
          @mouseenter-to-item="
            (index) => {
              if (!isKeyboardNavigating) hoveredDropdownItemIndex = index;
            }
          "
          @click-list-item="(item) => onClickDropdownItem(item)"
          @click-history-item="(item) => addTokenByHistory(item)"
          @remove-history-item="(item) => removeHistoryItem(item)"
        />
      </div>
    </div>
  </div>
</template>
<style lang="scss">
.filter-search {
  --filter-search-height: 28px;
  --filter-search-icon-width: 30px;
  --filter-search-clear-all-icon-width: 30px;
  --filter-search-logical-operator-width: 80px;

  display: flex;
  position: relative;
  flex-direction: row;
  gap: 4px;
  align-content: stretch;
  width: 100%;
  height: var(--filter-search-height) !important;
  background-color: var(--color-gray-09-00);

  &__logical-operator {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    width: 80px;
    border: 1px solid var(--color-gray-05-05);
    border-radius: 2px;

    .ev-input {
      height: 100% !important;
      border: none !important;
      font-size: 12px !important;
    }

    .ev-button {
      height: 100% !important;
      padding: 0 5px !important;
      border: none !important;
      border-radius: 0 !important;

      &:hover,
      &:active {
        background-color: var(--color-gray-04-04) !important;
      }
    }

    .ev-select {
      border-left: 1px solid var(--color-gray-05-05);
      border-radius: 0;
    }

    .ev-select,
    .ev-select__wrapper {
      height: 100%;
    }

    .ev-select-dropbox {
      z-index: 300;
      width: 75px !important;

      .disabled {
        font-size: 11px !important;
        color: var(--color-gray-02-10) !important;
      }

      &-item {
        font-size: 12px !important;
      }
    }
  }
  &__filter-main {
    display: inline-block;
    position: relative;
    width: calc(100% - var(--filter-search-logical-operator-width) - 4px);
    min-width: 0;
    border: 1px solid var(--color-gray-05-05);
    border-radius: 2px !important;
    transition: border 0.3s !important;

    &:hover {
      border-color: var(--color-gray-03-07);
    }

    &.focus {
      border-color: var(--color-blue-05-04) !important;
    }

    &.expand {
      position: absolute;
      top: 0;
      right: 0;
      left: calc(var(--filter-search-logical-operator-width) + 4px);
      z-index: 299;
      max-height: 53px;
      overflow-y: auto;
      background-color: var(--color-gray-09-00);
      box-shadow: var(--filter-search-dropdown-list-box-shadow);
    }

    &:not(.expand) {
      .filter-search__clear-all-btn-wrapper {
        padding-top: 3px;
      }
    }
  }
  &__filter-input-wrapper {
    display: flex;
    flex-grow: 0;
    gap: 4px;
    height: 100%;
    padding: 0 4px;

    .filter-search__filter-icon-wrapper {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: var(--filter-search-icon-width);
      height: 100%;
    }

    .filter-search__tokens-more-btn-wrapper {
      display: inline-flex;
      flex: 1;
      flex-flow: row;
      gap: 5px;
      align-items: center;
      height: auto;
      overflow: clip;
      cursor: text;

      &.expand {
        align-items: flex-start;
        overflow: visible;
      }
    }
  }
  &__tokens-wrapper {
    display: flex;
    gap: 5px;
    align-items: center;
    width: 100%;
    padding: 3px 0;

    .filter-search__token-wrapper {
      max-width: 100%;
    }

    .filter-search__main-input-wrapper {
      flex: auto;
      height: 20px;

      input {
        width: 100%;
        height: 100%;
        padding: 0 5px;
        outline: none;
        background-color: transparent !important;
        font-size: 12px;
        color: var(--main-font-color) !important;
        caret-color: transparent;

        &::placeholder {
          color: var(--color-gray-04-08);
        }
      }

      &:hover {
        border-color: var(--color-gray-03-07);
      }

      &::placeholder {
        color: var(--color-gray-03-09);
      }
    }
  }
  &__more-count-button-wrapper {
    position: sticky;
    top: 3px;
    flex-shrink: 0;
  }
  &__more-count-button {
    display: flex;
    gap: 2px;
    align-items: center;
    width: fit-content;
    padding: 2px 4px;
    cursor: pointer;
    border: 1px solid var(--color-gray-05-05);
    border-radius: 2px;
    background-color: var(--color-gray-06-02);
    font-size: 12px;
    color: var(--main-font-color);

    &:hover {
      background-color: var(--color-gray-04-05);
    }
  }
  &__clear-all-button {
    position: sticky;
    top: 3px;

    .icon-close {
      background-color: var(--color-gray-02-09) !important;
    }
  }
  &__dropdown-list-wrapper {
    position: fixed;
    z-index: 300;
    height: auto;
    max-height: 450px;
    overflow-y: auto;
    border-radius: 2px;
    background-color: var(--color-gray-06-00);
    box-shadow: var(--filter-search-dropdown-list-box-shadow);
  }

  &--active {
    .filter-search__logical-operator {
      .ev-button {
        background-color: var(--color-blue-09-01) !important;

        &:hover,
        &:active {
          background-color: var(--color-blue-08-02) !important;
        }
      }

      .icon-filter-list {
        background-color: var(--color-blue-05-05) !important;
      }
    }
  }

  &:not(&--active) {
    .filter-search__logical-operator {
      .ev-input {
        color: var(--color-gray-04-08) !important;
      }
    }

    .filter-search-token {
      border-color: var(--color-gray-05-05);

      .token-parts {
        &--key,
        &--operator,
        &--value {
          color: var(--color-gray-04-08) !important;
        }
      }
    }
  }
}
.ev-window,
.detail-information {
  .filter-search {
    line-height: normal;
  }
}
</style>
